import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { db, serverTimestamp } from "../../firebaseClient";
import { DataToolbar } from "../components/DataToolbar";
import { Modal } from "../components/Modal";
import { useToast } from "../components/Toast";
import { getDeptCodeByEmail, isSuperUser } from "../config/roles";
import { FileUploadKit } from "../components/common/FileUploadKit";
import { uploadGovernanceDocFiles } from "../services/storageUpload";
import * as DeptConst from "../constants/departments";
import { generateDocCode } from "../utils/idGenerator";
import { AIPlaceholderButton } from "../components/ai/AIPlaceholderButton";
import { analyzeUploadedFile } from "../services/aiService";

// ✅ 簽核流程（只加功能，不改既有 UI 佈局）
import { ApprovalFlowPanel } from "../components/approval/ApprovalFlowPanel";
import { useApprovalFlow } from "../hooks/useApprovalFlow";
import * as ApprovalFlowSvc from "../services/approvalFlowService";

const CORE_DOC_APPROVAL_MODULE = "GOVERNANCE_CORE_DOC";

const GOV_DOC_CATEGORIES = [
  "公司治理與董事會運作",
  "人資規範與勞動條件",
  "資訊安全與個資保護",
  "財務與會計／稅務管理",
  "其他",
];

// ✅ 核心文件：三關簽核程序（顯示名稱固定）
const CORE_DOC_APPROVAL_STAGES: any[] = [
  {
    key: "LEGAL_REVIEW",
    stageKey: "LEGAL_REVIEW",
    id: "LEGAL_REVIEW",
    title: "法務審查",
    name: "法務審查",
    displayName: "法務審查",
    order: 1,
    requiredRole: "LEGAL",
    approverRole: "LEGAL",
    assigneeRole: "LEGAL",
    role: "LEGAL",
    approverName: "法務審查",
  },
  {
    key: "COO_REVIEW",
    stageKey: "COO_REVIEW",
    id: "COO_REVIEW",
    title: "財務長",
    name: "財務長",
    displayName: "財務長",
    order: 2,
    // 內部角色碼若仍是 OPS 不改 service；顯示一律「財務長」
    requiredRole: "OPS",
    approverRole: "OPS",
    assigneeRole: "OPS",
    role: "OPS",
    approverName: "財務長",
  },
  {
    key: "GM_APPROVAL",
    stageKey: "GM_APPROVAL",
    id: "GM_APPROVAL",
    title: "總經理",
    name: "總經理",
    displayName: "總經理",
    order: 3,
    // 內部角色碼若仍是 GM 不改 service；顯示一律「總經理」
    requiredRole: "GM",
    approverRole: "GM",
    assigneeRole: "GM",
    role: "GM",
    approverName: "總經理",
  },
];

type DeptOption = { id: string; name: string; code?: string };

function safeArray<T = any>(v: any): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function getDeptNameByCodeSafe(code?: string): string {
  const anyDept: any = DeptConst as any;

  if (typeof anyDept.getDeptNameByCode === "function") {
    try {
      const v = anyDept.getDeptNameByCode(code);
      if (v) return v;
    } catch {
      // ignore
    }
  }

  const map = anyDept.DEPT_NAME_BY_CODE || anyDept.DEPT_NAME_MAP || anyDept.DEPT_MAP;
  if (map && code && map[code]) return map[code];

  return code || "";
}

function getDepartmentsForUI(): DeptOption[] {
  const anyDept: any = DeptConst as any;

  const arr =
    anyDept.DEPARTMENTS ||
    anyDept.departments ||
    anyDept.DEPT_LIST ||
    anyDept.DEPARTMENT_LIST ||
    anyDept.DEPTS;

  if (Array.isArray(arr)) {
    if (arr.length > 0 && typeof arr[0] === "string") {
      return arr.map((name: string, idx: number) => ({ id: String(idx + 1), name }));
    }
    if (arr.length > 0 && typeof arr[0] === "object") {
      return arr
        .map((d: any, idx: number) => ({
          id: String(d.id ?? idx + 1),
          name: String(d.name ?? d.label ?? d.title ?? d.code ?? ""),
          code: d.code,
        }))
        .filter((d: any) => !!d.name);
    }
  }

  const map = anyDept.DEPT_NAME_BY_CODE || anyDept.DEPT_NAME_MAP || anyDept.DEPT_MAP;
  if (map && typeof map === "object") {
    const codes = Object.keys(map);
    return codes.map((code: string, idx: number) => ({
      id: String(idx + 1),
      code,
      name: String(map[code] || code),
    }));
  }

  const codes =
    anyDept.DEPT_CODES || anyDept.DEPARTMENT_CODES || anyDept.DEPT_CODE_LIST || anyDept.DEPT_CODE;
  if (Array.isArray(codes)) {
    return codes.map((code: string, idx: number) => ({
      id: String(idx + 1),
      code,
      name: getDeptNameByCodeSafe(code) || String(code),
    }));
  }

  return [
    { id: "1", code: "LEGAL", name: "法務" },
    { id: "2", code: "OPS", name: "營運" },
    { id: "3", code: "FIN", name: "財務" },
    { id: "4", code: "HR", name: "人資" },
    { id: "5", code: "IT", name: "資訊" },
    { id: "6", code: "MKT", name: "行銷" },
  ];
}

const DEPARTMENTS: DeptOption[] = getDepartmentsForUI();

function statusLabel(status: string) {
  const v = String(status || "").toLowerCase();
  if (v === "published") return "正式公告";
  if (v === "in_review") return "簽核中";
  if (v === "returned") return "退回";
  if (v === "rejected") return "駁回";
  return "草稿";
}

function isDraftLike(status: any) {
  const v = String(status || "").toLowerCase();
  return v === "draft" || v === "returned" || v === "rejected" || v === "";
}

export function GovernanceCoreDocsPage() {
  const { user } = useAuth();
  const isSuper = isSuperUser(user?.email);
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState<"PUBLISHED" | "WORKSPACE">("PUBLISHED");
  const [items, setItems] = useState<any[]>([]);
  const [viewItem, setViewItem] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [openFlowOnOpen, setOpenFlowOnOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    const unsub = db
      .collection("governance_core_docs")
      .orderBy("updatedAt", "desc")
      .onSnapshot((snap: any) => {
        const all = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
        const filtered =
          activeTab === "PUBLISHED"
            ? all.filter((d: any) => String(d.status || "").toLowerCase() === "published")
            : all.filter((d: any) => String(d.status || "").toLowerCase() !== "published");
        setItems(filtered);
      });
    return () => unsub();
  }, [activeTab]);

  const filteredItems = useMemo(() => {
    const kw = String(q || "").trim().toLowerCase();
    if (!kw) return items;
    return items.filter((it: any) => {
      const hay = [
        it?.title,
        it?.docNo,
        it?.issuer,
        it?.ownerDept,
        it?.category,
        it?.status,
        it?.publishDate,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(kw);
    });
  }, [items, q]);

  const handleDeleteWorkspaceItem = async (item: any) => {
    if (!isSuper) return;
    if (!item?.id) return;

    const title = item?.title || item?.docNo || "未命名文件";
    if (!confirm(`確定要刪除「${title}」？此動作無法復原。`)) return;

    try {
      await db.collection("governance_core_docs").doc(item.id).delete();
      showToast("已刪除");
    } catch (e: any) {
      console.error(e);
      showToast("刪除失敗: " + (e?.message || "未知錯誤"), "error");
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="row" style={{ borderBottom: "1px solid #333", marginBottom: 12 }}>
        <button
          className={`navbtn ${activeTab === "PUBLISHED" ? "active" : ""}`}
          onClick={() => setActiveTab("PUBLISHED")}
        >
          正式公告
        </button>

        {isSuper && (
          <button
            className={`navbtn ${activeTab === "WORKSPACE" ? "active" : ""}`}
            onClick={() => setActiveTab("WORKSPACE")}
          >
            工作區（草稿/簽核中/退回/駁回）
          </button>
        )}

        <div style={{ flex: 1 }} />

        <input
          className="input"
          style={{ width: 360, height: 34, margin: "10px 0" }}
          placeholder="搜尋文件編號/標題/機關/部門..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="tile" style={{ padding: 14 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: "bold", color: "#fff", fontSize: 18 }}>
              公司治理核心文件庫（{activeTab === "PUBLISHED" ? "正式公告" : "工作區"}）
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
              {activeTab === "PUBLISHED"
                ? "提供各部門可查閱之公司治理核心制度文件。"
                : "草稿、簽核中、退回、駁回文件集中管理。"}
            </div>
          </div>

          <div className="row" style={{ gap: 10, alignItems: "center" }}>
            <button className="smallbtn" style={{ opacity: 0.9 }}>
              ✨ AI
            </button>
          </div>
        </div>

        {isSuper && activeTab === "WORKSPACE" && (
          <div className="row" style={{ justifyContent: "flex-end", marginTop: 12 }}>
            <button
              className="btn"
              style={{ width: "auto", background: "#fff", color: "#000", border: "none" }}
              onClick={() => {
                setViewItem(null);
                setOpenFlowOnOpen(false);
                setIsEditModalOpen(true);
              }}
            >
              ＋ 新增文件
            </button>
          </div>
        )}

        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          {filteredItems.map((item) => {
            const st = String(item?.status || "").toLowerCase();
            const showFlowBtn = activeTab === "WORKSPACE" && st !== "draft" && !!item?.id;

            return (
              <div
                key={item.id}
                className="tile"
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <div>
                  <div style={{ fontWeight: "bold", color: "#fff" }}>{item.title}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>
                    {item.docNo} | {statusLabel(item.status)}
                  </div>
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                    發布機關: {item.issuer} | 日期: {item.publishDate}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    className="smallbtn"
                    onClick={() => {
                      setViewItem(item);
                      setOpenFlowOnOpen(false);
                      setIsEditModalOpen(true);
                    }}
                  >
                    檢視
                  </button>

                  {/* ✅ 工作區：增加簽核流程檢視按鈕 */}
                  {showFlowBtn && (
                    <button
                      className="smallbtn"
                      onClick={() => {
                        setViewItem(item);
                        setOpenFlowOnOpen(true);
                        setIsEditModalOpen(true);
                      }}
                    >
                      簽核流程
                    </button>
                  )}

                  {isSuper && activeTab === "WORKSPACE" && (
                    <button
                      className="smallbtn"
                      style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
                      onClick={() => handleDeleteWorkspaceItem(item)}
                    >
                      刪除
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {filteredItems.length === 0 && (
            <div className="p" style={{ textAlign: "center", padding: 28, color: "#666" }}>
              目前無核心文件紀錄
            </div>
          )}
        </div>
      </div>

      {isEditModalOpen && (
        <CoreDocModal
          item={viewItem}
          onClose={() => {
            setIsEditModalOpen(false);
            setViewItem(null);
            setOpenFlowOnOpen(false);
          }}
          canEdit={isSuper}
          initialOpenFlow={openFlowOnOpen}
        />
      )}
    </div>
  );
}

function CoreDocModal({ item, onClose, canEdit, initialOpenFlow }: any) {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isSuper = isSuperUser(user?.email);

  const userDeptCode = getDeptCodeByEmail(user?.email);
  const userDeptName = getDeptNameByCodeSafe(userDeptCode);

  const [isFlowModalOpen, setIsFlowModalOpen] = useState(false);

  const normalizeItem = (src: any) => {
    if (!src) return null;
    return {
      ...src,
      attachments: safeArray(src.attachments),
      visibleDepartments: safeArray(src.visibleDepartments),
    };
  };

  const [form, setForm] = useState<any>(
    normalizeItem(item) || {
      title: "",
      docNo: "",
      ownerDept: userDeptName,
      description: "",
      status: "draft",
      category: GOV_DOC_CATEGORIES[0],
      attachments: [],
      issuer: "",
      publishDate: new Date().toISOString().split("T")[0],
      visibleDepartments: isSuper ? ["all"] : [userDeptName],
    }
  );

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const visibleDepartments = safeArray<string>(form.visibleDepartments);
  const attachments = safeArray<any>(form.attachments);

  const flowTargetId = item?.id || "";
  const { flow: approvalFlow, loading: approvalFlowLoading } = useApprovalFlow(
    CORE_DOC_APPROVAL_MODULE,
    flowTargetId
  );

  // ✅ 簽核中鎖定：起單人/任何人都只能看，不能改資料（直到退回/駁回）
  const currentStatus = String(item?.status ?? form?.status ?? "").toLowerCase();
  const editable = canEdit && (!item?.id || isDraftLike(currentStatus));

  useEffect(() => {
    if (!item && !form.docNo) {
      db.collection("governance_core_docs")
        .get()
        .then((snap: any) => {
          const code = generateDocCode("governance", new Date(), snap.docs.map((d: any) => d.data()));
          setForm((prev: any) => ({ ...prev, docNo: code }));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  useEffect(() => {
    if (initialOpenFlow && item?.id) {
      setIsFlowModalOpen(true);
    }
  }, [initialOpenFlow, item?.id]);

  const toggleDept = (dept: string) => {
    if (!isSuper) return;
    if (!editable) return;

    const current = safeArray<string>(form.visibleDepartments);

    if (dept === "all") {
      setForm({ ...form, visibleDepartments: ["all"] });
      return;
    }

    const filtered = current.filter((d) => d !== "all");
    if (filtered.includes(dept)) {
      setForm({ ...form, visibleDepartments: filtered.filter((d) => d !== dept) });
    } else {
      setForm({ ...form, visibleDepartments: [...filtered, dept] });
    }
  };

  const handleSave = async () => {
    if (!editable) return;

    if (!form.title || !form.issuer || !form.publishDate || safeArray(form.visibleDepartments).length === 0) {
      alert("請填寫必填欄位 (標題、機關、日期、可視部門)");
      return;
    }

    setIsSaving(true);
    try {
      let finalAttachments = [...attachments];

      if (pendingFiles.length > 0) {
        const uploaded = await uploadGovernanceDocFiles(item?.id || "temp_" + Date.now(), pendingFiles);
        finalAttachments = [...finalAttachments, ...uploaded];
      }

      const payload = {
        ...form,
        attachments: finalAttachments,
        visibleDepartments: safeArray(form.visibleDepartments),
        updatedAt: serverTimestamp(),
      };

      if (item?.id) {
        await db.collection("governance_core_docs").doc(item.id).update(payload);
      } else {
        await db.collection("governance_core_docs").add({
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      showToast("儲存成功");
      onClose();
    } catch (e: any) {
      console.error(e);
      showToast("儲存失敗: " + (e?.message || "未知錯誤"), "error");
    } finally {
      setIsSaving(false);
    }
  };


  const safeText = (v: any) => (typeof v === "string" ? v.trim() : "");

  const handleAiAnalyze = async () => {
    if (!editable) return;

    const instruction = [
      "你是企業內部法務AI助理。請務必以『實際附件內容』為準（不要只看標題或欄位文字）。",
      "輸出請分成兩段：",
      "1) 務實法務版：白話摘要、條款/義務重點、風險點、需要補件/修訂清單、談判建議（偏進攻談判）。",
      "2) 管理報告版：3–7點重點摘要、風險等級（高/中/低與理由）、需要主管決策的點與建議方向。",
      "並嘗試抽取可回填欄位：標題、日期、金額（如有）、關鍵對象/機關（如有）。",
    ].join("\n");

    setAiBusy(true);
    try {
      let res: any = null;

      if (pendingFiles.length > 0) {
        const mode: any = pendingFiles.length >= 2 ? "batch_analysis" : "analysis";
        res = await analyzeUploadedFile({
          mode,
          module: "governance_core_docs",
          userInstruction: instruction,
          currentFields: {
            title: form.title,
            issuer: form.issuer,
            publishDate: form.publishDate,
            category: form.category,
            docNo: form.docNo,
          },
          files: pendingFiles,
        } as any);
      } else if (attachments.length > 0) {
        const a0: any = attachments[0];
        const url = safeText(a0?.url || a0?.downloadUrl || a0?.downloadURL);
        if (!url) {
          showToast("附件缺少下載連結，請重新上傳後再試一次", "error");
          return;
        }
        res = await analyzeUploadedFile({
          mode: "analysis",
          module: "governance_core_docs",
          userInstruction: instruction,
          currentFields: {
            title: form.title,
            issuer: form.issuer,
            publishDate: form.publishDate,
            category: form.category,
            docNo: form.docNo,
          },
          fileMeta: {
            name: a0.name || "attachment",
            mimeType: a0.contentType || "",
            downloadUrl: url,
            url,
          },
        } as any);
      } else {
        showToast("請先上傳附件後再按 AI 分析", "error");
        return;
      }

      if (!res?.ok) throw new Error(res?.message || "AI 分析失敗");

      const fields = (res as any).fields || {};
      const next: any = { ...form };

      // 回填：標題 / 日期（僅在欄位空白時補上，避免覆蓋手動輸入）
      const title = safeText(fields.title);
      const mainDate = safeText(fields.mainDate);
      if (!safeText(next.title) && title) next.title = title;
      if (!safeText(next.publishDate) && mainDate) next.publishDate = mainDate;

      // 回填：摘要（不覆蓋，改用追加）
      const summary = safeText(res.summary);
      if (summary) {
        next.description = [safeText(next.description), summary].filter(Boolean).join("\n\n");
      }

      setForm(next);
      showToast("AI 分析已回填");
    } catch (e: any) {
      console.error(e);
      showToast("AI 分析失敗: " + (e?.message || "未知錯誤"), "error");
    } finally {
      setAiBusy(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!isSuper) return;
    if (!item?.id) {
      showToast("請先儲存文件後再送呈核", "error");
      return;
    }

    // 只能在草稿/退回/駁回時送呈核
    if (!isDraftLike(item?.status)) {
      showToast("此文件目前非草稿狀態，無法再次送呈核", "error");
      return;
    }

    const title = item?.title || item?.docNo || "未命名文件";
    if (!confirm(`確定要送呈核「${title}」？送出後將鎖定內容，需退回/駁回後才能修改。`)) return;

    try {
      await db.collection("governance_core_docs").doc(item.id).update({
        status: "in_review",
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const fn: any =
        (ApprovalFlowSvc as any).createOrResetApprovalFlow ||
        (ApprovalFlowSvc as any).createOrResetFlow ||
        (ApprovalFlowSvc as any).createApprovalFlow;

      if (typeof fn !== "function") {
        showToast("簽核服務缺少 createOrResetApprovalFlow（文件已標記為簽核中）", "error");
        return;
      }

      const requesterUid = (user as any)?.id || (user as any)?.uid || "unknown";
      const requesterName =
        (user as any)?.displayName || (user as any)?.name || (user as any)?.email || "unknown";

      // ✅ 不動 service：盡量用 service 既有支援的呼叫型態，把 stages 帶進去
      if (typeof fn.length === "number" && fn.length >= 6) {
        await fn(
          CORE_DOC_APPROVAL_MODULE,
          item.id,
          requesterUid,
          userDeptName,
          requesterName,
          CORE_DOC_APPROVAL_STAGES
        );
      } else if (typeof fn.length === "number" && fn.length === 1) {
        await fn({
          module: CORE_DOC_APPROVAL_MODULE,
          targetId: item.id,
          requesterUid,
          requesterDept: userDeptName,
          requesterName,
          stages: CORE_DOC_APPROVAL_STAGES,
        });
      } else {
        await fn(
          CORE_DOC_APPROVAL_MODULE,
          item.id,
          requesterUid,
          userDeptName,
          requesterName,
          { stages: CORE_DOC_APPROVAL_STAGES }
        );
      }

      showToast("已送呈核");
      setIsFlowModalOpen(true);
    } catch (e: any) {
      console.error(e);
      showToast("送呈核失敗: " + (e?.message || "未知錯誤"), "error");
    }
  };

  const handleFlowDecision = async (...args: any[]) => {
    if (!approvalFlow || !item?.id) return;

    const stageKey = args[0];
    const decision = args[1];
    const comment = args[2] ?? "";
    const deptCode = args[3];
    const isDirectSign = args[4];

    const updateStageDecision: any =
      (ApprovalFlowSvc as any).updateStageDecision ||
      (ApprovalFlowSvc as any).updateApprovalStageDecision ||
      (ApprovalFlowSvc as any).updateDecision;

    if (typeof updateStageDecision !== "function") {
      showToast("簽核服務缺少 updateStageDecision", "error");
      return;
    }

    try {
      await updateStageDecision({
        flowId: (approvalFlow as any).id,
        stageKey,
        decision,
        comment,
        actorUid: (user as any)?.id || (user as any)?.uid || "unknown",
        actorName: (user as any)?.displayName || (user as any)?.name || (user as any)?.email || "unknown",
        actorEmail: (user as any)?.email || "",
        deptCode,
        isDirectSign,
      });

      const stages = ((approvalFlow as any)?.stages || []) as any[];
      const last = stages.length
        ? stages[stages.length - 1]
        : CORE_DOC_APPROVAL_STAGES[CORE_DOC_APPROVAL_STAGES.length - 1];

      const lastKey = last?.key || last?.stageKey || last?.id || null;
      const isLastStage = lastKey && String(stageKey) === String(lastKey);

      if (decision === "REJECTED") {
        await db.collection("governance_core_docs").doc(item.id).update({
          status: "rejected",
          updatedAt: serverTimestamp(),
        });
      } else if (decision === "RETURNED") {
        await db.collection("governance_core_docs").doc(item.id).update({
          status: "returned",
          updatedAt: serverTimestamp(),
        });
      } else if (decision === "APPROVED" && isLastStage) {
        await db.collection("governance_core_docs").doc(item.id).update({
          status: "published",
          approvedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        await db.collection("governance_core_docs").doc(item.id).update({
          status: "in_review",
          updatedAt: serverTimestamp(),
        });
      }

      showToast("已送出決策");
    } catch (e: any) {
      console.error(e);
      showToast("送出決策失敗: " + (e?.message || "未知錯誤"), "error");
    }
  };

  return (
    <>
      <Modal title={item ? "編輯核心文件" : "新增核心文件"} onClose={onClose} width={800}>
        <div className="grid">
          <div className="field" style={{ gridColumn: "1/-1" }}>
            <label className="label">文件標題 *</label>
            <input
              className="input"
              value={form.title || ""}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              disabled={!editable}
            />
          </div>

          <div className="field">
            <label className="label">文件編號</label>
            <input
              className="input"
              value={form.docNo || ""}
              readOnly
              style={{ background: "#111", color: "#fbbf24" }}
            />
          </div>

          <div className="field">
            <label className="label">類別</label>
            <select
              className="input"
              value={form.category || GOV_DOC_CATEGORIES[0]}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              disabled={!editable}
            >
              {GOV_DOC_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="label">發布機關 *</label>
            <input
              className="input"
              value={form.issuer || ""}
              onChange={(e) => setForm({ ...form, issuer: e.target.value })}
              disabled={!editable}
              placeholder="例如：董事會、總經理室"
            />
          </div>

          <div className="field">
            <label className="label">發布日期 *</label>
            <input
              type="date"
              className="input"
              value={form.publishDate || ""}
              onChange={(e) => setForm({ ...form, publishDate: e.target.value })}
              disabled={!editable}
            />
          </div>

          <div className="field" style={{ gridColumn: "1/-1" }}>
            <label className="label">可檢視部門 *</label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                background: "rgba(255,255,255,0.03)",
                padding: 12,
                borderRadius: 8,
              }}
            >
              {isSuper ? (
                <>
                  <label
                    className="badge"
                    style={{
                      background: visibleDepartments.includes("all") ? "#3b82f6" : "#333",
                      cursor: editable ? "pointer" : "not-allowed",
                      opacity: editable ? 1 : 0.6,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={visibleDepartments.includes("all")}
                      onChange={() => editable && toggleDept("all")}
                      style={{ display: "none" }}
                      disabled={!editable}
                    />
                    全公司
                  </label>

                  {DEPARTMENTS.map((d) => {
                    const active = visibleDepartments.includes(d.name) && !visibleDepartments.includes("all");
                    return (
                      <label
                        key={d.id}
                        className="badge"
                        style={{
                          background: active ? "#3b82f6" : "#333",
                          cursor: editable ? "pointer" : "not-allowed",
                          opacity: editable ? 1 : 0.6,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={visibleDepartments.includes(d.name)}
                          onChange={() => editable && toggleDept(d.name)}
                          style={{ display: "none" }}
                          disabled={!editable}
                        />
                        {d.name}
                      </label>
                    );
                  })}
                </>
              ) : (
                <div className="p" style={{ fontSize: 13 }}>
                  {safeArray<string>(form.visibleDepartments).join(", ")}
                </div>
              )}
            </div>
          </div>

          <div className="field" style={{ gridColumn: "1/-1" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <label className="label" style={{ marginBottom: 0 }}>
                說明 / 摘要
              </label>
              <AIPlaceholderButton type="summary" disabled={!editable || aiBusy} onClick={handleAiAnalyze}>
                {aiBusy ? "AI 分析中…" : "AI 分析"}
              </AIPlaceholderButton>
            </div>
            <textarea
              className="input"
              rows={3}
              value={form.description || ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              disabled={!editable}
            />
          </div>
        </div>

        <FileUploadKit
          label="文件附件"
          existingFiles={attachments}
          rawFiles={pendingFiles}
          onFilesSelected={(fs: File[]) => editable && setPendingFiles((prev) => [...prev, ...fs])}
          onRemoveRawFile={(idx: number) => editable && setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
          onRemoveFile={(_f: any, i: number) => {
            if (!editable) return;
            const next = [...attachments];
            next.splice(i, 1);
            setForm({ ...form, attachments: next });
          }}
          disabled={!editable || isSaving}
        />

        <div className="row" style={{ marginTop: 24, justifyContent: "flex-end", gap: 12 }}>
          <button
            className="btn"
            style={{ width: "auto", background: "#f3f4f6", border: "1px solid #d1d5db", color: "#111827" }}
            onClick={onClose}
          >
            取消
          </button>

          {item?.id && (
            <button
              className="btn"
              style={{ width: "auto", background: "rgba(255,255,255,0.04)", border: "1px solid #666", color: "#e5e7eb" }}
              onClick={() => setIsFlowModalOpen(true)}
            >
              簽核流程
            </button>
          )}

          {isSuper && item?.id && isDraftLike(item?.status) && (
            <button
              className="btn"
              style={{ width: "auto", background: "#fff", color: "#000", border: "none" }}
              onClick={handleSubmitForApproval}
            >
              送呈核
            </button>
          )}

          {editable && (
            <button
              className="btn"
              style={{ width: "auto", background: "#3b82f6", color: "#fff", border: "none" }}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "儲存中..." : "儲存文件"}
            </button>
          )}
        </div>

        {/* ✅ 顯示鎖定提醒（不改 UI 佈局，只補一行文字） */}
        {!editable && item?.id && String(item?.status || "").toLowerCase() === "in_review" && (
          <div className="p" style={{ marginTop: 10, color: "var(--muted)", fontSize: 12 }}>
            此文件目前為「簽核中」，內容已鎖定，需退回/駁回後才能修改。
          </div>
        )}
      </Modal>

      {isFlowModalOpen && (
        <Modal title="簽核流程" onClose={() => setIsFlowModalOpen(false)} width={900}>
          {approvalFlowLoading && (
            <div className="p" style={{ color: "var(--muted)", padding: 12 }}>
              載入中…
            </div>
          )}

          {!approvalFlowLoading && !approvalFlow && (
            <div className="p" style={{ color: "var(--muted)", padding: 12 }}>
              尚未建立簽核流程（請先送呈核）
            </div>
          )}

          {!approvalFlowLoading && approvalFlow && (
            <ApprovalFlowPanel flow={approvalFlow as any} mode="action" onDecision={handleFlowDecision as any} />
          )}
        </Modal>
      )}
    </>
  );
}
